import {
  Checkbox,
  CheckboxProps,
  createForm,
  Flex,
  Heading,
  Select,
  TextInput,
  TextInputProps,
  PhoneInput,
  PhoneInputProps,
} from '@applyboard/crystal-ui'
import {
  ContactInformation,
  RelationshipType as ContactRelation,
} from '@applyboard/school-applications-types-lib'
import { isEmpty, omitBy, set } from 'lodash'
import { useGetProfile, useLoggedInUser } from '../../../hooks'
import { RawApplicationResponse } from '../../../hooks/useGetApplication'
import { useUpdateApplication } from '../../../hooks/useUpdateApplication'
import { emailValidate } from '../../../utils/fieldValidations'
import { Loading } from '../../Loading'
import { RelationshipType } from '../../../utils/enums'
import { StudentApplication } from '../types'
import { GenericError } from '../../../utils'
import { ComponentType } from 'react'

type ContactInformationFormFields = {
  phone: string
  alternatePhone: string
  email: string
  provideEmergencyContact: boolean
  emergencyName: string
  emergencyRelationship: string
  emergencyPhone: string
  emergencyEmail: string
}

const { Form, Field, useFieldValues } = createForm<ContactInformationFormFields>()

type ContactInformationTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: GenericError) => void
  updateApplication: ReturnType<typeof useUpdateApplication>['updateApplication']
  formId: string
}

export function ContactInformationTab(props: ContactInformationTabProps) {
  const { username } = useLoggedInUser()
  const { isLoadingProfile, profile } = useGetProfile({ id: username })

  if (isLoadingProfile || !profile) {
    return <Loading />
  }

  return (
    <Form
      id={props.formId}
      defaultValues={buildDefaultValues(props.application, profile?.attributes.email)}
      onSubmit={data => {
        if (props.disabled) {
          props.onSuccess()
        } else {
          const contactInformation: Omit<ContactInformation, 'email'> = {
            phoneNumber: data.phone,
            // email: profile.attributes.email,
            alternatePhoneNumber: data.alternatePhone,
          }
          const personalInformation = {
            contactInformation,
          }

          if (data.provideEmergencyContact) {
            set(personalInformation, 'emergencyContact', {
              name: data.emergencyName,
              relationship: data.emergencyRelationship as ContactRelation,
              email: data.emergencyEmail,
              phoneNumber: data.emergencyPhone,
            })
          } else {
            set(personalInformation, 'emergencyContact', null)
          }

          props.updateApplication(
            {
              attributes: {
                personalInformation,
              },
            },
            {
              onSuccess: props.onSuccess,
              onError: props.onError,
            },
          )
        }
      }}
    >
      <ContactInformationFields disabled={props.disabled} application={props.application} />
    </Form>
  )
}

type ContactInformationFieldsProps = {
  disabled?: boolean
  application: StudentApplication
}

function ContactInformationFields(props: ContactInformationFieldsProps) {
  const { provideEmergencyContact, phone, emergencyPhone } = useFieldValues([
    'provideEmergencyContact',
    'phone',
    'emergencyPhone',
  ])

  function getPhoneRequired(disabled?: boolean, value?: string) {
    if (disabled) return false
    return value && value.length > 0 ? 'Invalid phone number' : 'Phone number is required'
  }

  return (
    <>
      <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={PhoneInput as ComponentType<PhoneInputProps>}
            label="Phone number"
            name="phone"
            required={getPhoneRequired(props.disabled, phone)}
            disabled={props.disabled}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={PhoneInput as ComponentType<PhoneInputProps>}
            label="Alternate phone number"
            name="alternatePhone"
            disabled={props.disabled}
          />
        </Flex.Item>
        <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
          <Field
            as={TextInput as ComponentType<TextInputProps>}
            label="Email"
            name="email"
            disabled
          />
        </Flex.Item>
        {!props.disabled || haveEmergencyContact(props.application) ? (
          <Flex basis="100%" pt={{ xs: 6, sm: 0 }}>
            <Heading variant="titleS" level={3}>
              <span aria-hidden>🚨</span> Emergency Contact
            </Heading>
          </Flex>
        ) : null}
        {!props.disabled ? (
          <Flex.Item basis={{ xs: '100%' }}>
            <Field
              as={Checkbox as ComponentType<CheckboxProps>}
              label="Would you like to provide an emergency contact?"
              name="provideEmergencyContact"
            />
          </Flex.Item>
        ) : null}
        {provideEmergencyContact ? (
          <>
            <Flex.Item basis={{ xs: '100%' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Name"
                name="emergencyName"
                helpText="Full name of your emergency contact."
                disabled={props.disabled}
                required={!props.disabled ? 'Name is required' : false}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {/* //TODO: This field becomes a Select and use RelationshipCode from Application. Update with Aurora lastest changes */}
              <Field
                as={Select}
                label="Relationship"
                name="emergencyRelationship"
                disabled={props.disabled}
                appearance="styled"
                required={!props.disabled ? 'Relationship is required' : false}
              >
                <Select.Option label="Parent" value={RelationshipType.PARENT} />
                <Select.Option label="Guardian" value={RelationshipType.GUARDIAN} />
                <Select.Option label="Friend" value={RelationshipType.FRIEND} />
                <Select.Option label="Spouse" value={RelationshipType.SPOUSE} />
                <Select.Option label="Relative" value={RelationshipType.RELATIVE} />
                <Select.Option label="Other" value={RelationshipType.OTHER} />
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={PhoneInput as ComponentType<PhoneInputProps>}
                label="Phone number"
                name="emergencyPhone"
                disabled={props.disabled}
                required={getPhoneRequired(props.disabled, emergencyPhone)}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Email"
                name="emergencyEmail"
                validate={(value: string) => {
                  if (!!value && !emailValidate(value)) {
                    return 'This email is invalid.'
                  }

                  return true
                }}
                disabled={props.disabled}
                required={!props.disabled ? 'Email is required' : false}
              />
            </Flex.Item>
          </>
        ) : null}
      </Flex>
    </>
  )
}

function haveEmergencyContact(application: StudentApplication) {
  if (!application?.attributes?.personalInformation?.emergencyContact) {
    return false
  }

  return !isEmpty(
    omitBy(application?.attributes?.personalInformation?.emergencyContact, v => !v?.length),
  )
}

function buildDefaultValues(application: StudentApplication, email: string | undefined) {
  return {
    phone: application?.attributes?.personalInformation?.contactInformation?.phoneNumber || '',
    alternatePhone:
      application?.attributes?.personalInformation?.contactInformation?.alternatePhoneNumber || '',
    email: email || '',
    provideEmergencyContact: haveEmergencyContact(application),
    emergencyName: application?.attributes?.personalInformation?.emergencyContact?.name || '',
    emergencyRelationship:
      application?.attributes?.personalInformation?.emergencyContact?.relationship || '',
    emergencyPhone:
      application?.attributes?.personalInformation?.emergencyContact?.phoneNumber || '',
    emergencyEmail: application?.attributes?.personalInformation?.emergencyContact?.email || '',
  }
}
